import { useState, useEffect } from 'react'
import { X, AlertTriangle, Clock, CreditCard, Zap } from 'lucide-react'

export default function TrialReminderModal({ 
  isOpen, 
  onClose, 
  daysRemaining, 
  trialEndDate, 
  planName = 'Pro',
  onUpgrade 
}) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
    }
  }, [isOpen])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      onClose()
    }, 300)
  }

  const getUrgencyLevel = (days) => {
    if (days <= 2) return 'critical'
    if (days <= 5) return 'high'
    if (days <= 10) return 'medium'
    return 'low'
  }

  const getUrgencyConfig = (level) => {
    switch (level) {
      case 'critical':
        return {
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          iconColor: 'text-red-600',
          buttonColor: 'bg-red-600 hover:bg-red-700',
          title: 'Trial Expires Soon!'
        }
      case 'high':
        return {
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          iconColor: 'text-orange-600',
          buttonColor: 'bg-orange-600 hover:bg-orange-700',
          title: 'Trial Ending Soon'
        }
      case 'medium':
        return {
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          iconColor: 'text-yellow-600',
          buttonColor: 'bg-yellow-600 hover:bg-yellow-700',
          title: 'Trial Reminder'
        }
      default:
        return {
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          iconColor: 'text-blue-600',
          buttonColor: 'bg-blue-600 hover:bg-blue-700',
          title: 'Trial Reminder'
        }
    }
  }

  if (!isOpen) return null

  const urgencyLevel = getUrgencyLevel(daysRemaining)
  const config = getUrgencyConfig(urgencyLevel)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className={`relative transform overflow-hidden rounded-lg ${config.bgColor} ${config.borderColor} border-2 shadow-xl transition-all ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}>
          <div className="px-6 py-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-full ${config.iconColor.replace('text-', 'bg-').replace('-600', '-100')}`}>
                  {urgencyLevel === 'critical' ? (
                    <AlertTriangle className="h-6 w-6" />
                  ) : (
                    <Clock className="h-6 w-6" />
                  )}
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${config.iconColor}`}>
                    {config.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {planName} Trial Reminder
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="mb-6">
              <div className="text-center mb-4">
                <div className={`inline-flex items-center px-4 py-2 rounded-full text-2xl font-bold ${config.iconColor} ${config.iconColor.replace('text-', 'bg-').replace('-600', '-100')}`}>
                  {daysRemaining === 1 ? '1 Day' : `${daysRemaining} Days`} Left
                </div>
              </div>
              
              <p className="text-gray-700 text-center mb-4">
                Your <strong>{planName} trial</strong> will expire on{' '}
                <strong>{trialEndDate.toLocaleDateString()}</strong>.
              </p>

              <div className="bg-white rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-900 mb-2">What happens when your trial ends:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Your account will return to the free tier</li>
                  <li>• You'll lose access to Pro features</li>
                  <li>• Any invited organization admins will lose their access</li>
                  <li>• Groups and alerts will be limited to free tier amounts</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Pro features you'll lose:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Zap className="h-4 w-4 text-green-500 mr-2" />
                    <span>Up to 5 groups</span>
                  </div>
                  <div className="flex items-center">
                    <Zap className="h-4 w-4 text-green-500 mr-2" />
                    <span>100 alerts per month</span>
                  </div>
                  <div className="flex items-center">
                    <Zap className="h-4 w-4 text-green-500 mr-2" />
                    <span>2 organization admins</span>
                  </div>
                  <div className="flex items-center">
                    <Zap className="h-4 w-4 text-green-500 mr-2" />
                    <span>Advanced features</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onUpgrade}
                className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg text-white font-medium transition-colors ${config.buttonColor}`}
              >
                <CreditCard className="h-5 w-5 mr-2" />
                Upgrade to {planName}
              </button>
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Remind Me Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
